import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MARKET, pickMarketRows } from "@/lib/market";

const SOCIAL_KEYS = [
  "facebook_url",
  "instagram_url",
  "linkedin_url",
  "youtube_url",
] as const;

export type SocialKey = (typeof SOCIAL_KEYS)[number];
export type SocialLinksMap = Partial<Record<SocialKey, string>>;

const normalize = (value?: string | null) => {
  const v = (value ?? "").trim();
  return v.length > 0 ? v : undefined;
};

/**
 * Fonte única para links sociais do site.
 * Preferência: app_settings (Configurações /admin/configuracoes).
 * Fallback: footer_settings (compatibilidade retroativa).
 */
export function useSocialLinks() {
  return useQuery({
    queryKey: ["app-settings", "social-links"],
    queryFn: async (): Promise<SocialLinksMap> => {
      const { data: appData, error: appError } = await supabase
        .from("app_settings")
        .select("setting_key, setting_value")
        .in("setting_key", [...SOCIAL_KEYS]);

      if (appError) throw appError;

      const appMap: Record<string, string> = {};
      appData?.forEach((row) => {
        appMap[row.setting_key] = row.setting_value;
      });

      const hasAnyAppValue = SOCIAL_KEYS.some((k) => !!normalize(appMap[k]));

      const result: SocialLinksMap = {};
      if (hasAnyAppValue) {
        SOCIAL_KEYS.forEach((k) => {
          const v = normalize(appMap[k]);
          if (v) result[k] = v;
        });
        return result;
      }

      // Fallback (projetos antigos): footer_settings
      const { data: footerData, error: footerError } = await supabase
        .from("footer_settings")
        .select("setting_key, setting_value, market")
        .in("setting_key", [...SOCIAL_KEYS])
        .in("market", [MARKET, "pt"]);

      if (footerError) throw footerError;

      pickMarketRows(footerData as any, MARKET).forEach((row) => {
        const key = row.setting_key as SocialKey;
        if (!SOCIAL_KEYS.includes(key)) return;
        const v = normalize(row.setting_value);
        if (v) result[key] = v;
      });

      return result;
    },
    staleTime: 60 * 1000,
  });
}
